<template>
    <div class="q-pa-md">
        <q-toolbar class="text-white shadow-2" style="background:rgb(39 170 225);">
            <q-toolbar-title>CREATE a FREE Memo Link</q-toolbar-title>
        </q-toolbar>
        <q-stepper
            ref="stepper"
            v-model="currentStep"
            :keep-alive="true"
            :vertical="quasar.screen.xs"
            animated
            color="primary"
            done-color="positive"
            class="q-pt-md"
            error-icon="fa-solid fa-triangle-exclamation"
            flat
        >
            <q-step
                :active-icon="stepperData[0].icon"
                :done="stepperData[0].done"
                :error="stepperData[0].error"
                :name="stepperData[0].name"
                :title="stepperData[0].title"
                error-icon="fa-solid fa-triangle-exclamation"
                prefix="5"
            >
                <Step1 ref="Step1Form" @next-step="nextStep"></Step1>
            </q-step>

            <q-step
                :active-icon="stepperData[1].icon"
                :done="stepperData[1].done"
                :error="stepperData[1].error"
                :name="stepperData[1].name"
                :title="stepperData[1].title"
                prefix="2"
            >
                <Step2 ref="Step2Form" @next-step="nextStep"></Step2>
            </q-step>

            <q-step
                :done="stepperData[2].done"
                :error="stepperData[2].error"
                :name="stepperData[2].name"
                :title="stepperData[2].title"
                active-color="positive"
                active-icon="fa-solid fa-check"
                prefix="3"
            >
                <Step3 :memo-link="memoLink"></Step3>
            </q-step>

            <template v-slot:navigation>
                <q-stepper-navigation>
                    <q-btn v-if="currentStep < 3" color="primary" label="Continue" @click="nextStep"/>
                    <q-btn v-if="currentStep > 1 && currentStep < 3" class="q-ml-sm" color="primary" flat
                           label="Back"
                           @click="prevStep"/>
                </q-stepper-navigation>
            </template>
        </q-stepper>
    </div>
</template>

<script>
export default {
    name: 'Create Memo Link'
}
</script>

<script setup>
import { inject, ref, toRaw } from 'vue'
import { useQuasar } from 'quasar'

const quasar = useQuasar()
import Step1 from './steps/Step1'
import Step2 from './steps/Step2'
import Step3 from './steps/Step3'
import { event } from 'vue-gtag'

const currentStep = ref(1)
const stepper = ref(null)
const Step1Form = ref(null)
const Step2Form = ref(null)

const url = ref('')
const words = ref([])
const stepperData = ref([
    {
        name: 1,
        icon: 'fa-solid fa-1',
        title: 'Add your URL',
        done: false,
        error: false
    },
    {
        name: 2,
        icon: 'fa-solid fa-2',
        title: 'Enter Keywords / Domain',
        done: false,
        error: false
    },
    {
        name: 3,
        icon: 'fa-solid fa-3',
        title: 'Get your Memo Link',
        done: false,
        error: false
    },
])
const memoLink = ref({})

const eventBus = inject('eventBus')
const axios = inject('axios')

async function nextStep () {
    let isValid = false
    switch (currentStep.value) {
        case 1:
            isValid = await checkFirstStep()
            break
        case 2:
            isValid = await checkSecondStep()
            break
    }

    if (isValid) {
        stepperData.value[currentStep.value - 1].done = true
        stepperData.value[currentStep.value - 1].error = false
        stepper.value.next()
    } else {
        stepperData.value[currentStep.value - 1].error = true
        stepperData.value[currentStep.value - 1].done = false
    }
}

function prevStep () {
    stepperData.value[currentStep.value - 1].error = false
    stepperData.value[currentStep.value - 1].done = false
    stepperData.value[currentStep.value - 2].done = false
    stepper.value.previous()
}

async function checkFirstStep () {
    let isValid = await Step1Form.value.$refs.form.validate()

    if (!isValid) {
        return false
    }

    const formData = new FormData(Step1Form.value.$refs.form.$el)

    url.value = formData.get('url')

    return true
}

async function checkSecondStep () {
    Step2Form.value.words.forEach((word, index) => {
        Step2Form.value.hasError[index] = false
        Step2Form.value.errorMessages[index] = null
    })

    let isValid = await Step2Form.value.$refs.form.validate()

    if (!isValid) {
        return false
    }

    const formData = new FormData(Step2Form.value.$refs.form.$el)
    words.value = formData.getAll('memo_link_word[]').map(word => word.trim())
    const subdomainId = formData.get('subdomain')

    if (!subdomainId && words.value.join('').match(/^\d+$/)) {
        Step2Form.value.words.forEach((word, index) => {
            Step2Form.value.hasError[index] = true
        })
        quasar.notify({
            progress: true,
            message: 'All tags cannot be only numbers at the same time',
            type: 'negative',
            position: 'top',
            multiLine: true
        })

        return false
    }

    quasar.loading.show()
    const response = await axios
        .post('memo-link', {
            url: url.value,
            memo_link_word: toRaw(words).value,
            subdomain: subdomainId,
        })
        .then((response) => {
            if (response?.name === 'AxiosError') {
                for (const [field, errors] of Object.entries(response?.response?.data?.errors ?? {})) {
                    if (field.match('memo_link_word')) {
                        let index = parseInt(field.charAt(field.length - 1))
                        Step2Form.value.hasError[index] = true
                        Step2Form.value.errorMessages[index] = errors[0]
                    } else if (field === 'duplicate_link' || field === 'unique_word') {
                        Step2Form.value.words.forEach((word, index) => {
                            Step2Form.value.hasError[index] = true
                            Step2Form.value.errorMessages[index] = 'To keep this Word change at least another one'
                        })
                    }

                    quasar.notify({
                        progress: true,
                        message: errors[0],
                        type: 'negative',
                        position: 'top',
                        multiLine: true
                    })
                }
            } else {
                event('memo-link-create', { method: 'Google' })
                return response.data.data
            }
        })
        .catch(() => {
            quasar.notify({
                progress: true,
                message: 'An error occurred',
                type: 'negative',
                position: 'top',
                multiLine: true
            })
        })
        .finally(() => {
            quasar.loading.hide()
        })

    if (response) {
        memoLink.value = response
        return true
    }

    return false
}

function resetForm () {
    currentStep.value = 1
    words.value = []
    url.value = ''

    stepperData.value.forEach(data => {
        data.done = false
        data.error = false
    })

    if (Step1Form.value) {
        Step1Form.value.url = ''
    }

    if (Step2Form.value) {
        Step2Form.value.words = []
    }

    setTimeout(() => {
        Step1Form?.value?.$refs?.form?.resetValidation()
        Step2Form?.value?.$refs?.form?.resetValidation()
    })
}

eventBus.on('reset-ml-form', resetForm)
</script>

<style>
.q-stepper__tab.q-stepper__tab--error-with-icon .q-stepper__dot {
    background: var(--q-negative) !important;
    color: white !important;
}

.q-stepper__tab.q-stepper__tab--error-with-icon .q-stepper__dot span {
    font-size: 14px;
}

.q-stepper {
    background: #ffffff;
    border-width: 1px;
    border-style: solid;
    border-color: #a3a3a3;
    border-radius: 0 4px 4px 4px !important;
    z-index: 1;
}

.q-stepper__nav {
    padding: 14px 24px 24px !important;
}
</style>

<style scoped>
.q-toolbar__title {
    font-size: 14px;
}
.q-stepper{
    border-top: none;
}
</style>
